import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import toast from 'react-hot-toast';

import {
  ShareExpertLinkResponse,
  UnlockedEmailResult,
  assignExperts,
  clientApprovedStatus,
  createShareExpertLink,
  deleteExpert,
  getClientSubmittedExperts,
  getExperts,
  getUnlockedEmail,
  // gettSubmittedExperts, // shared-link public URL disabled
  // publicApprovedStatus, // shared-link public URL disabled
  searchCompanies,
  searchExpertTitles,
  searchExperts,
  updateExpertStatus,
} from '@/api';

import { IUpdateStatusResponse } from '@/interface';
import { CreateShareExpertBody, Expert, PaginatedExpertResponse } from '@/interface';

const toErrorMessage = (error: unknown, fallbackMessage: string): string => {
  const message = axios.isAxiosError(error) ? (error.response?.data as { message?: string } | undefined)?.message : undefined;
  return message || fallbackMessage;
};

interface ExpertState {
  loading: boolean;
  loadingUnlockedEmail: boolean;
  loadingAssignProject: boolean;
  error: string | null;
  experts: Expert[];
  searchResults: PaginatedExpertResponse | null;
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  submittedExperts: Expert[];
  submittedExpertsProjectId: string | null;
  loadingSubmittedExperts: boolean;
  companySearchOptions: string[];
  titleSearchOptions: string[];
}

const initialState: ExpertState = {
  loading: false,
  loadingUnlockedEmail: false,
  loadingAssignProject: false,
  submittedExperts: [],
  submittedExpertsProjectId: null,
  loadingSubmittedExperts: false,
  error: null,
  searchResults: null,
  companySearchOptions: [],
  titleSearchOptions: [],
  experts: [],
  total: 0,
  page: 1,
  limit: 10,
  totalPages: 0,
};

export const getExpertsRequest = createAsyncThunk<
  PaginatedExpertResponse,
  { page: number; limit: number },
  { rejectValue: string }
>('experts/fetchAll', async ({ page = 1, limit = 10 }, { rejectWithValue }) => {
  try {
    const response = await getExperts(page, limit);
    return response.data;
  } catch (error) {
    return rejectWithValue(toErrorMessage(error, 'Failed to fetch experts'));
  }
});
export const deleteExpertRequest = createAsyncThunk<{ id: string }, string, { rejectValue: string }>(
  'experts/delete',
  async (id, { rejectWithValue }) => {
    try {
      const response = await deleteExpert(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(toErrorMessage(error, 'Failed to delete expert'));
    }
  },
);
export const getUnlockedEmailRequest = createAsyncThunk<UnlockedEmailResult[], string[], { rejectValue: string }>(
  'experts/getUnlockedEmail',
  async (ids, { rejectWithValue }) => {
    try {
      const response = await getUnlockedEmail(ids);
      return response.data;
    } catch (error) {
      return rejectWithValue(toErrorMessage(error, 'Failed to fetch unlocked emails'));
    }
  },
);
// getSubmittedExpertsRequest — disabled (shared-link public URL removed)
// export const getSubmittedExpertsRequest = createAsyncThunk<
//   Expert[],
//   { expertIds: string; jwtToken?: string },
//   { rejectValue: string }
// >('experts/getSelectedExperts', async ({ expertIds, jwtToken }, { rejectWithValue }) => {
//   try {
//     const response = await gettSubmittedExperts(expertIds, jwtToken);
//     return response.data;
//   } catch (error) {
//     return rejectWithValue(toErrorMessage(error, 'Failed to fetch selected experts'));
//   }
// });

export const getClientSubmittedExpertsRequest = createAsyncThunk<
  { projectId: string; experts: Expert[] },
  string,
  { rejectValue: string }
>('experts/getClientSubmittedExperts', async (projectId, { rejectWithValue }) => {
  try {
    const response = await getClientSubmittedExperts(projectId);
    return { projectId, experts: response.data };
  } catch (error) {
    return rejectWithValue(toErrorMessage(error, 'Failed to fetch submitted experts'));
  }
});

export const getSearchCompanies = createAsyncThunk<string[], { values: string }, { rejectValue: string }>(
  'experts/getSearchCompanies',
  async ({ values }, { rejectWithValue }) => {
    try {
      const response = await searchCompanies(values);
      return response.data;
    } catch (error) {
      return rejectWithValue(toErrorMessage(error, 'Failed to fetch selected experts'));
    }
  },
);
export const getSearchExpertTitles = createAsyncThunk<
  string[],
  { values: string; companies?: string[] },
  { rejectValue: string }
>('experts/getSearchExpertTitles', async ({ values, companies }, { rejectWithValue }) => {
  try {
    const response = await searchExpertTitles(values, companies);
    return response.data;
  } catch (error) {
    return rejectWithValue(toErrorMessage(error, 'Failed to fetch selected experts'));
  }
});
interface UpdateStatusPayload {
  projectId: string;
  expertIds: string[];
  status?: string;
  jwtToken?: string;
}

const applyExpertStatusUpdate = (
  expert: Expert,
  update: { expertIds?: string[]; projectExpertIds?: string[]; status?: string; clientStatus?: string | null },
): Expert => {
  const matchesExpertId = update.expertIds?.includes(expert.id);
  const matchesProjectExpertId = expert.projectExpertId ? update.projectExpertIds?.includes(expert.projectExpertId) : false;
  if (!matchesExpertId && !matchesProjectExpertId) return expert;

  return {
    ...expert,
    ...(update.status !== undefined ? { status: update.status } : {}),
    ...(update.clientStatus !== undefined ? { clientStatus: update.clientStatus } : {}),
  };
};

export const updateExpertStatusRequest = createAsyncThunk<IUpdateStatusResponse, UpdateStatusPayload, { rejectValue: string }>(
  'experts/updateStatus',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateExpertStatus(payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(toErrorMessage(error, 'Failed to update expert status'));
    }
  },
);
export const createShareExpertLinkRequest = createAsyncThunk<
  ShareExpertLinkResponse,
  CreateShareExpertBody,
  { rejectValue: string }
>('experts/createShareExpertLinkRequest', async (payload, { rejectWithValue }) => {
  try {
    const response = await createShareExpertLink(payload);
    return response.data;
  } catch (error) {
    return rejectWithValue(toErrorMessage(error, 'Failed to submit experts to client'));
  }
});
export const assignExpertsRequest = createAsyncThunk<unknown, UpdateStatusPayload, { rejectValue: string }>(
  'experts/assignExperts',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await assignExperts(payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(toErrorMessage(error, 'Failed to assign expert'));
    }
  },
);

// publicApprovedStatusRequest — disabled (shared-link public URL generation removed)
// export const publicApprovedStatusRequest = createAsyncThunk<IUpdateStatusResponse, UpdateStatusPayload, { rejectValue: string }>(
//   'experts/publicApprovedStatus',
//   async (payload, { rejectWithValue }) => {
//     try {
//       const response = await publicApprovedStatus(payload);
//       return response.data;
//     } catch (error) {
//       return rejectWithValue(toErrorMessage(error, 'Failed to update expert status'));
//     }
//   },
// );

export const clientApprovedStatusRequest = createAsyncThunk<IUpdateStatusResponse, UpdateStatusPayload, { rejectValue: string }>(
  'experts/clientApprovedStatus',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await clientApprovedStatus(payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(toErrorMessage(error, 'Failed to update expert status'));
    }
  },
);
// Search People Request
export const searchExpertsRequest = createAsyncThunk<
  PaginatedExpertResponse,
  { data: Record<string, unknown> },
  { rejectValue: string }
>('expert/searchExperts', async ({ data }, { rejectWithValue }) => {
  try {
    const response = await searchExperts(data);
    return response.data;
  } catch (error) {
    return rejectWithValue(toErrorMessage(error, 'Failed to search people'));
  }
});
export const expertSlice = createSlice({
  name: 'experts',
  initialState,
  reducers: {
    updateExpertStatusNoti: (
      state,
      action: PayloadAction<{
        expertIds: string[];
        projectExpertIds?: string[];
        status?: string;
        clientStatus?: string | null;
        clearClientStatus?: boolean;
      }>,
    ) => {
      const { expertIds, projectExpertIds, status, clearClientStatus } = action.payload;
      const clientStatus = clearClientStatus ? null : action.payload.clientStatus;
      state.experts = state.experts.map((expert) =>
        applyExpertStatusUpdate(expert, { expertIds, projectExpertIds, status, clientStatus }),
      );
      if (state.submittedExperts.length > 0) {
        state.submittedExperts = state.submittedExperts.map((expert) =>
          applyExpertStatusUpdate(expert, { expertIds, projectExpertIds, status, clientStatus }),
        );
      }
    },
    resetExpertError: (state) => {
      state.error = null;
    },
    resetSubmittedExperts: (state) => {
      state.submittedExperts = [];
      state.submittedExpertsProjectId = null;
      state.loadingSubmittedExperts = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getExpertsRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getExpertsRequest.fulfilled, (state, action) => {
        const { data, total, page, limit, totalPages } = action.payload;
        state.loading = false;
        state.experts = data;
        state.total = total;
        state.page = page;
        state.limit = limit;
        state.totalPages = totalPages ?? Math.ceil(total / limit);
      })
      .addCase(getExpertsRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Something went wrong';
      })
      .addCase(deleteExpertRequest.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteExpertRequest.fulfilled, (state, action) => {
        state.loading = false;
        if (state.experts?.length) {
          state.experts = state.experts.filter((expert) => expert.id !== action.payload.id);
        }
        // state.total -= 1;
      })
      .addCase(deleteExpertRequest.rejected, (state, action) => {
        state.loading = false;
        toast.error('Failed to delete expert');
        state.error = action.payload || 'Failed to delete expert';
      })
      // Handle Search People Request
      .addCase(searchExpertsRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchExpertsRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.experts = action.payload?.data || [];
        state.searchResults = action.payload;
      })
      .addCase(searchExpertsRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to search people';
      })
      .addCase(getUnlockedEmailRequest.pending, (state) => {
        state.loadingUnlockedEmail = true;
        state.error = null;
      })
      .addCase(getUnlockedEmailRequest.fulfilled, (state, action) => {
        state.loadingUnlockedEmail = false;
        const updatedExperts = action.payload;
        state.experts = state.experts.map((expert) => {
          const updated = updatedExperts.find((e) => e.apolloId === expert.apolloId);
          return updated ? { ...expert, email: updated.email } : expert;
        });
      })
      .addCase(getUnlockedEmailRequest.rejected, (state, action) => {
        state.loadingUnlockedEmail = false;
        state.error = action.payload || 'Failed to fetch unlocked emails';
      })
      .addCase(getClientSubmittedExpertsRequest.pending, (state, action) => {
        state.loadingSubmittedExperts = true;
        state.submittedExperts = [];
        state.submittedExpertsProjectId = action.meta.arg;
        state.error = null;
      })
      .addCase(getClientSubmittedExpertsRequest.fulfilled, (state, action) => {
        if (state.submittedExpertsProjectId !== action.payload.projectId) return;
        state.loadingSubmittedExperts = false;
        state.submittedExperts = action.payload.experts;
      })
      .addCase(getClientSubmittedExpertsRequest.rejected, (state, action) => {
        if (state.submittedExpertsProjectId !== action.meta.arg) return;
        state.loadingSubmittedExperts = false;
        state.submittedExperts = [];
        state.error = action.payload || 'Failed to fetch submitted experts';
      })
      .addCase(updateExpertStatusRequest.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateExpertStatusRequest.fulfilled, (state, action) => {
        state.loading = false;
        action.payload.experts?.forEach((expertStatus) => {
          const update = {
            expertIds: [expertStatus.id],
            projectExpertIds: expertStatus.projectExpertId ? [expertStatus.projectExpertId] : undefined,
            status: expertStatus.status,
            clientStatus: expertStatus.clientStatus,
          };
          state.experts = state.experts.map((expert) => applyExpertStatusUpdate(expert, update));
          state.submittedExperts = state.submittedExperts.map((expert) => applyExpertStatusUpdate(expert, update));
        });
      })
      .addCase(updateExpertStatusRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to update expert status';
      })
      .addCase(clientApprovedStatusRequest.pending, (state) => {
        state.loading = true;
      })
      .addCase(clientApprovedStatusRequest.fulfilled, (state, action) => {
        state.loading = false;
        action.payload.experts?.forEach((expertStatus) => {
          const update = {
            expertIds: [expertStatus.id],
            projectExpertIds: expertStatus.projectExpertId ? [expertStatus.projectExpertId] : undefined,
            status: expertStatus.status,
            clientStatus: expertStatus.clientStatus,
          };
          state.experts = state.experts.map((expert) => applyExpertStatusUpdate(expert, update));
          state.submittedExperts = state.submittedExperts.map((expert) => applyExpertStatusUpdate(expert, update));
        });
      })
      .addCase(clientApprovedStatusRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to update expert status';
      })
      .addCase(assignExpertsRequest.pending, (state) => {
        state.loadingAssignProject = true;
      })
      .addCase(assignExpertsRequest.fulfilled, (state) => {
        state.loadingAssignProject = false;
      })
      .addCase(assignExpertsRequest.rejected, (state, action) => {
        state.loadingAssignProject = false;
        state.error = action.payload as string;
      })
      .addCase(getSearchExpertTitles.pending, (state) => {
        state.loadingSubmittedExperts = true;
      })
      .addCase(getSearchExpertTitles.fulfilled, (state, action) => {
        state.loadingSubmittedExperts = false;
        state.titleSearchOptions = action.payload;
      })
      .addCase(getSearchExpertTitles.rejected, (state, action) => {
        state.loadingSubmittedExperts = false;
        state.error = action.payload || 'Failed to fetch selected experts';
      })
      .addCase(getSearchCompanies.pending, (state) => {
        state.loadingSubmittedExperts = true;
      })
      .addCase(getSearchCompanies.fulfilled, (state, action) => {
        state.loadingSubmittedExperts = false;
        state.companySearchOptions = action.payload;
      })
      .addCase(getSearchCompanies.rejected, (state, action) => {
        state.loadingSubmittedExperts = false;
        state.error = action.payload || 'Failed to fetch selected experts';
      });
  },
});

export const { updateExpertStatusNoti, resetExpertError, resetSubmittedExperts } = expertSlice.actions;
